import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Clock,
  ExternalLink,
  FolderKanban,
  MessageSquare,
  Play,
  Send,
  X,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { ErrorState } from '@/components/ErrorState';
import { SkeletonText } from '@/components/Skeleton';
import {
  adicionarComentarioDeTarefa,
  atualizarTarefa,
  listarComentariosDeTarefa,
  minhasTarefas,
  type ComentarioDeTarefa,
  type TarefaMinhaAtividade,
} from '@/features/projetos/projetos.api';
import { cn } from '@/lib/cn';

/* ── helpers ─────────────────────────────────────────────────────────────── */

function fmtData(ep: number | null): string {
  if (!ep) return '—';
  return new Date(ep * 60 * 1000).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

function fmtDataHora(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function isDelayed(t: TarefaMinhaAtividade): boolean {
  const nowMin = Math.floor(Date.now() / 60_000);
  return t.progress < 100 && t.finishAt !== null && t.finishAt < nowMin;
}

/* ── StatusChip ──────────────────────────────────────────────────────────── */

function StatusChip({ tarefa }: { tarefa: TarefaMinhaAtividade }) {
  const delayed = isDelayed(tarefa);
  if (delayed)
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2.5 py-0.5 text-xs font-medium text-destructive">
        <AlertTriangle className="h-3 w-3" /> Atrasada
      </span>
    );
  if (tarefa.isCritical)
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-cyan-100 px-2.5 py-0.5 text-xs font-medium text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400">
        <AlertTriangle className="h-3 w-3" /> Crítica
      </span>
    );
  if (tarefa.progress === 100)
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
        <CheckCircle2 className="h-3 w-3" /> Concluída
      </span>
    );
  if (tarefa.progress > 0)
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
        <Clock className="h-3 w-3" /> Em andamento
      </span>
    );
  return (
    <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
      A fazer
    </span>
  );
}

/* ── Modal de tarefa ─────────────────────────────────────────────────────── */

type Tab = 'detalhes' | 'comentarios';

function ModalTarefa({
  tarefa,
  onClose,
}: {
  tarefa: TarefaMinhaAtividade;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [aba, setAba] = useState<Tab>('detalhes');
  const [progresso, setProgresso] = useState(tarefa.progress);
  const [novoComentario, setNovoComentario] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data: comentarios, isLoading: loadingComentarios } = useQuery({
    queryKey: ['tarefa-comentarios', tarefa.id],
    queryFn: () => listarComentariosDeTarefa(tarefa.project.id, tarefa.id),
    enabled: aba === 'comentarios',
  });

  const mutAtualizar = useMutation({
    mutationFn: (p: number) =>
      atualizarTarefa(tarefa.project.id, tarefa.id, { progress: p }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['minhas-tarefas'] });
    },
  });

  const mutComentar = useMutation({
    mutationFn: (body: string) =>
      adicionarComentarioDeTarefa(tarefa.project.id, tarefa.id, body),
    onSuccess: () => {
      setNovoComentario('');
      void qc.invalidateQueries({ queryKey: ['tarefa-comentarios', tarefa.id] });
    },
  });

  const handleIniciar = () => {
    setProgresso(1);
    mutAtualizar.mutate(1);
  };

  const handleConcluir = () => {
    setProgresso(100);
    mutAtualizar.mutate(100);
  };

  const handleProgresso = (v: number) => {
    const clamped = Math.max(0, Math.min(100, v));
    setProgresso(clamped);
    mutAtualizar.mutate(clamped);
  };

  const handleEnviarComentario = () => {
    const body = novoComentario.trim();
    if (!body) return;
    mutComentar.mutate(body);
  };

  const delayed = isDelayed(tarefa);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-lg rounded-xl bg-background shadow-xl border border-border flex flex-col max-h-[90vh]">
        {/* Cabeçalho */}
        <div className="flex items-start justify-between gap-3 p-4 border-b border-border shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-muted-foreground">{tarefa.outlineNumber}</span>
              <StatusChip tarefa={{ ...tarefa, progress: progresso }} />
            </div>
            <h2 className="mt-1 text-base font-semibold leading-tight">{tarefa.name}</h2>
            <Link
              to="/projetos/$projetoId"
              params={{ projetoId: tarefa.project.id }}
              className="inline-flex items-center gap-1 mt-1 text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              <FolderKanban className="h-3 w-3" />
              {tarefa.project.number} {tarefa.project.name}
              <ExternalLink className="h-3 w-3" />
            </Link>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 rounded-md p-1 hover:bg-muted text-muted-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Abas */}
        <div className="flex border-b border-border shrink-0">
          {(['detalhes', 'comentarios'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setAba(t)}
              className={cn(
                'px-4 py-2 text-sm font-medium transition-colors',
                aba === t
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t === 'detalhes' ? 'Detalhes' : 'Comentários'}
              {t === 'comentarios' && comentarios && comentarios.length > 0 && (
                <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-xs">
                  {comentarios.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Corpo */}
        <div className="flex-1 overflow-y-auto">
          {aba === 'detalhes' && (
            <div className="p-4 flex flex-col gap-4">
              {/* Info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {tarefa.team && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-0.5">Equipe</span>
                    <span className="font-medium">{tarefa.team.name}</span>
                  </div>
                )}
                {tarefa.finishAt && (
                  <div>
                    <span className="text-xs text-muted-foreground block mb-0.5">Prazo</span>
                    <span className={cn('font-medium', delayed && 'text-destructive')}>
                      {fmtData(tarefa.finishAt - 1)}
                    </span>
                  </div>
                )}
              </div>

              {/* Progresso */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Progresso</span>
                  <span className="text-sm tabular-nums text-muted-foreground">{progresso}%</span>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${progresso}%` }}
                  />
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={progresso}
                  onChange={(e) => setProgresso(Number(e.target.value))}
                  onMouseUp={(e) => handleProgresso(Number((e.target as HTMLInputElement).value))}
                  onTouchEnd={(e) => handleProgresso(Number((e.target as HTMLInputElement).value))}
                  className="w-full mt-2 accent-primary"
                />
              </div>

              {/* Ações */}
              <div className="flex gap-2 flex-wrap">
                {progresso === 0 && (
                  <button
                    onClick={handleIniciar}
                    disabled={mutAtualizar.isPending}
                    className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60 transition-colors"
                  >
                    <Play className="h-4 w-4" />
                    Iniciar tarefa
                  </button>
                )}
                {progresso > 0 && progresso < 100 && (
                  <button
                    onClick={handleConcluir}
                    disabled={mutAtualizar.isPending}
                    className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60 transition-colors"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Marcar como concluída
                  </button>
                )}
                <button
                  onClick={() => setAba('comentarios')}
                  className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted transition-colors"
                >
                  <MessageSquare className="h-4 w-4" />
                  Comentários
                </button>
              </div>
            </div>
          )}

          {aba === 'comentarios' && (
            <div className="flex flex-col h-full">
              {/* Lista */}
              <div className="flex-1 p-4 flex flex-col gap-3 overflow-y-auto min-h-[200px]">
                {loadingComentarios && (
                  <div className="flex flex-col gap-2">
                    <SkeletonText className="h-12 w-full" />
                    <SkeletonText className="h-12 w-3/4" />
                  </div>
                )}
                {!loadingComentarios && (!comentarios || comentarios.length === 0) && (
                  <div className="text-center py-8">
                    <MessageSquare className="mx-auto mb-2 h-6 w-6 text-muted-foreground/40" />
                    <p className="text-sm text-muted-foreground">Nenhum comentário ainda.</p>
                  </div>
                )}
                {comentarios?.map((c: ComentarioDeTarefa) => (
                  <div key={c.id} className="rounded-lg border border-border bg-muted/20 p-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-medium">
                        {c.author?.displayName ?? c.author?.name ?? 'Sistema'}
                      </span>
                      <span className="text-xs text-muted-foreground">{fmtDataHora(c.createdAt)}</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{c.body}</p>
                  </div>
                ))}
              </div>

              {/* Entrada de comentário */}
              <div className="p-4 border-t border-border shrink-0">
                <div className="flex gap-2">
                  <textarea
                    ref={textareaRef}
                    value={novoComentario}
                    onChange={(e) => setNovoComentario(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();
                        handleEnviarComentario();
                      }
                    }}
                    placeholder="Adicionar comentário… (Ctrl+Enter para enviar)"
                    rows={2}
                    className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <button
                    onClick={handleEnviarComentario}
                    disabled={!novoComentario.trim() || mutComentar.isPending}
                    className="shrink-0 rounded-lg bg-primary px-3 text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── agrupamento e ordenação ─────────────────────────────────────────────── */

function groupByProject(tarefas: TarefaMinhaAtividade[]): Map<string, TarefaMinhaAtividade[]> {
  const map = new Map<string, TarefaMinhaAtividade[]>();
  for (const t of tarefas) {
    if (!map.has(t.project.id)) map.set(t.project.id, []);
    map.get(t.project.id)!.push(t);
  }
  return map;
}

function sortPriority(a: TarefaMinhaAtividade, b: TarefaMinhaAtividade): number {
  const score = (t: TarefaMinhaAtividade) =>
    (isDelayed(t) ? 4 : 0) + (t.isCritical ? 2 : 0) + (t.progress > 0 && t.progress < 100 ? 1 : 0);
  const diff = score(b) - score(a);
  if (diff !== 0) return diff;
  return (a.finishAt ?? Infinity) - (b.finishAt ?? Infinity);
}

/* ── página ──────────────────────────────────────────────────────────────── */

type FiltroAtividade = 'todas' | 'atrasadas' | 'andamento' | 'afazer' | 'concluidas';

export function MinhasTarefasPage() {
  const qc = useQueryClient();
  const [tarefaAberta, setTarefaAberta] = useState<TarefaMinhaAtividade | null>(null);
  const [filtro, setFiltro] = useState<FiltroAtividade>('todas');

  const { data: tarefas, isLoading, isError, refetch } = useQuery({
    queryKey: ['minhas-tarefas'],
    queryFn: minhasTarefas,
  });

  const mutIniciar = useMutation({
    mutationFn: ({ projetoId, tarefaId }: { projetoId: string; tarefaId: string }) =>
      atualizarTarefa(projetoId, tarefaId, { progress: 1 }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['minhas-tarefas'] }),
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <SkeletonText className="h-6 w-48" />
        <SkeletonText className="h-40 w-full" />
      </div>
    );
  }

  if (isError) {
    return <ErrorState error={null} title="Erro ao carregar atividades" onRetry={() => void refetch()} />;
  }

  const lista = tarefas ?? [];
  const atrasadas = lista.filter(isDelayed);
  const criticas = lista.filter((t) => !isDelayed(t) && t.isCritical);

  const counts = {
    todas: lista.length,
    atrasadas: lista.filter(isDelayed).length,
    andamento: lista.filter((t) => !isDelayed(t) && t.progress > 0 && t.progress < 100).length,
    afazer: lista.filter((t) => !isDelayed(t) && t.progress === 0).length,
    concluidas: lista.filter((t) => t.progress === 100).length,
  };

  const listaFiltrada = lista.filter((t) => {
    if (filtro === 'todas') return true;
    if (filtro === 'atrasadas') return isDelayed(t);
    if (filtro === 'andamento') return !isDelayed(t) && t.progress > 0 && t.progress < 100;
    if (filtro === 'afazer') return !isDelayed(t) && t.progress === 0;
    if (filtro === 'concluidas') return t.progress === 100;
    return true;
  });

  const grupos = groupByProject(listaFiltrada);

  return (
    <>
      <div className="flex flex-col gap-6 p-4 sm:p-6">
        <div>
          <h1 className="text-lg font-semibold">Minhas Atividades</h1>
          <p className="text-sm text-muted-foreground">Tarefas de projetos nas quais você está alocado.</p>
        </div>

        {/* Abas de filtro */}
        <div className="flex gap-1 overflow-x-auto border-b border-border pb-0 -mx-4 px-4 sm:-mx-6 sm:px-6">
          {(
            [
              { id: 'todas', label: 'Todas' },
              { id: 'atrasadas', label: 'Atrasadas' },
              { id: 'andamento', label: 'Em andamento' },
              { id: 'afazer', label: 'A fazer' },
              { id: 'concluidas', label: 'Concluídas' },
            ] as { id: FiltroAtividade; label: string }[]
          ).map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => setFiltro(id)}
              className={cn(
                'flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                filtro === id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {label}
              {counts[id] > 0 && (
                <span
                  className={cn(
                    'rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums',
                    filtro === id
                      ? id === 'atrasadas'
                        ? 'bg-destructive/10 text-destructive'
                        : 'bg-primary/10 text-primary'
                      : 'bg-muted text-muted-foreground',
                  )}
                >
                  {counts[id]}
                </span>
              )}
            </button>
          ))}
        </div>

        {(atrasadas.length > 0 || criticas.length > 0) && (
          <div className="flex flex-wrap gap-3">
            {atrasadas.length > 0 && (
              <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-2.5">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                <span className="text-sm font-medium text-destructive">
                  {atrasadas.length} tarefa{atrasadas.length > 1 ? 's' : ''} atrasada{atrasadas.length > 1 ? 's' : ''}
                </span>
              </div>
            )}
            {criticas.length > 0 && (
              <div className="flex items-center gap-2 rounded-lg border border-cyan-300 bg-cyan-50 px-4 py-2.5 dark:border-cyan-700 dark:bg-cyan-900/20">
                <AlertTriangle className="h-4 w-4 text-cyan-700 dark:text-cyan-400" />
                <span className="text-sm font-medium text-cyan-700 dark:text-cyan-400">
                  {criticas.length} tarefa{criticas.length > 1 ? 's' : ''} no caminho crítico
                </span>
              </div>
            )}
          </div>
        )}

        {/* Barra de resumo */}
        {lista.length > 0 && (
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
            <span>
              <span className="font-semibold tabular-nums text-foreground">{lista.length}</span> tarefas no total
            </span>
            {counts.concluidas > 0 && (
              <span>
                <span className="font-semibold tabular-nums text-green-600">{counts.concluidas}</span> concluídas
              </span>
            )}
            {counts.andamento > 0 && (
              <span>
                <span className="font-semibold tabular-nums text-primary">{counts.andamento}</span> em andamento
              </span>
            )}
            {counts.atrasadas > 0 && (
              <span>
                <span className="font-semibold tabular-nums text-destructive">{counts.atrasadas}</span> atrasadas
              </span>
            )}
          </div>
        )}

        {listaFiltrada.length === 0 && lista.length > 0 ? (
          <div className="rounded-xl border border-border bg-muted/10 px-6 py-10 text-center">
            <p className="text-sm text-muted-foreground">Nenhuma tarefa neste filtro.</p>
          </div>
        ) : lista.length === 0 ? (
          <div className="rounded-xl border border-border bg-muted/10 px-6 py-12 text-center">
            <FolderKanban className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
            <p className="text-sm font-medium text-muted-foreground">Nenhuma tarefa atribuída a você.</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Quando um PMO alocar você em uma tarefa de projeto, ela aparecerá aqui.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {Array.from(grupos.entries()).map(([_projectId, tasks]) => {
              const primeiro = tasks[0];
              if (!primeiro) return null;
              const projeto = primeiro.project;
              const sortedTasks = [...tasks].sort(sortPriority);
              const hasAlert = tasks.some((t) => isDelayed(t) || t.isCritical);

              return (
                <div
                  key={projeto.id}
                  className="rounded-xl border border-border bg-background shadow-sm overflow-hidden"
                >
                  {/* Cabeçalho do projeto */}
                  <div
                    className={cn(
                      'flex items-center justify-between px-4 py-3 border-b border-border',
                      hasAlert ? 'bg-muted/20' : 'bg-muted/10',
                    )}
                  >
                    <Link
                      to="/projetos/$projetoId"
                      params={{ projetoId: projeto.id }}
                      className="flex items-center gap-2 group"
                    >
                      <FolderKanban className="h-4 w-4 text-primary shrink-0" />
                      <div>
                        <span className="text-xs font-mono text-muted-foreground">{projeto.number}</span>
                        <span className="ml-2 text-sm font-semibold group-hover:text-primary transition-colors">
                          {projeto.name}
                        </span>
                      </div>
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                    <span className="text-xs text-muted-foreground">
                      {tasks.length} tarefa{tasks.length > 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Lista de tarefas */}
                  <ul className="divide-y divide-border">
                    {sortedTasks.map((t) => {
                      const delayed = isDelayed(t);
                      return (
                        <li
                          key={t.id}
                          className={cn(
                            'flex items-start gap-3 px-4 py-3 group cursor-pointer hover:bg-muted/5 transition-colors',
                            delayed
                              ? 'bg-destructive/3'
                              : t.isCritical
                                ? 'bg-cyan-50/50 dark:bg-cyan-900/10'
                                : '',
                          )}
                          onClick={() => setTarefaAberta(t)}
                        >
                          {/* Indicador */}
                          <div
                            className={cn(
                              'mt-1 h-2 w-2 rounded-full shrink-0',
                              delayed
                                ? 'bg-destructive'
                                : t.isCritical
                                  ? 'bg-cyan-500'
                                  : t.progress === 100
                                    ? 'bg-green-500'
                                    : t.progress > 0
                                      ? 'bg-primary'
                                      : 'bg-muted-foreground/30',
                            )}
                          />

                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
                              <span
                                className={cn(
                                  'text-sm font-medium',
                                  delayed
                                    ? 'text-destructive'
                                    : t.isCritical
                                      ? 'text-cyan-700 dark:text-cyan-400'
                                      : '',
                                )}
                              >
                                <span className="font-mono text-xs text-muted-foreground mr-1">
                                  {t.outlineNumber}
                                </span>
                                {t.name}
                              </span>
                              <StatusChip tarefa={t} />
                            </div>

                            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                              {t.team && <span>{t.team.name}</span>}
                              {t.finishAt && (
                                <span className={cn(delayed ? 'text-destructive font-medium' : '')}>
                                  Término: {fmtData(t.finishAt - 1)}
                                </span>
                              )}
                            </div>

                            {t.progress > 0 && t.progress < 100 && (
                              <div className="mt-2 flex items-center gap-2">
                                <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-primary transition-all"
                                    style={{ width: `${t.progress}%` }}
                                  />
                                </div>
                                <span className="text-xs tabular-nums text-muted-foreground w-8 text-right">
                                  {t.progress}%
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Ações rápidas */}
                          <div
                            className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {t.progress === 0 && (
                              <button
                                title="Iniciar tarefa"
                                onClick={() =>
                                  mutIniciar.mutate({ projetoId: t.project.id, tarefaId: t.id })
                                }
                                disabled={mutIniciar.isPending}
                                className="rounded-md p-1.5 text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors disabled:opacity-50"
                              >
                                <Play className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              title="Abrir detalhes"
                              onClick={() => setTarefaAberta(t)}
                              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted transition-colors"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {tarefaAberta && (
        <ModalTarefa tarefa={tarefaAberta} onClose={() => setTarefaAberta(null)} />
      )}
    </>
  );
}
